import { computeTrainStats } from './trainStats';
import { df, uc } from './dates';
import { computeGoalProgress } from './goals';
// Date constants computed locally — NO import dependency, CANNOT be undefined
const _d = new Date();
const TODAY      = _d.toISOString().slice(0, 10);
const TODAY_DISP = _d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric",year:"numeric"}).toUpperCase();
const TODAY_LONG = _d.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});

// ─── SIGNALS / INSIGHTS / BRIEF / SYSTEM PROMPT ───────────────────────────────
function buildSignals(s) {
  console.log('%c[SIGNALS] buildSignals called', 'color:#4a9e74', {onboarded: s.profile?.onboarded, hasBudgets: !!s.budgets});
  const out = [];
  if (!s.profile?.onboarded) return out;

  // Guard all arrays against undefined (corrupted/old state schemas)
  const vinyl    = s.vinyl    || [];
  const tasks    = s.tasks    || [];
  const finance  = s.finance  || [];
  const events   = s.events   || [];
  const workouts = s.workouts || [];
  const goals    = s.goals    || [];

  vinyl.filter(v => !v.delivered && v.status==="shipped").forEach(v => {
    const d = df(v.eta);
    if (d >= 0 && d <= 5) out.push({ id:`vs${v.id}`, lvl:"info", type:"vinyl", msg:`${v.artist} — ${v.album} arriving ${d===0?"today":"in "+d+"d"}` });
  });

  Object.entries(s.budgets || {}).forEach(([cat, b]) => {
    const p = b.spent / b.limit;
    if (p >= 0.9)  out.push({ id:`bo${cat}`, lvl:"warn", type:"finance", msg:`${uc(cat)} budget ${Math.round(p*100)}% — ${b.spent>b.limit?"over":"$"+(b.limit-b.spent).toFixed(0)+" left"}` });
    else if (p >= 0.78) out.push({ id:`bw${cat}`, lvl:"warn", type:"finance", msg:`${uc(cat)} budget at ${Math.round(p*100)}% — nearing limit` });
  });

  // Only warn about shift schedule if user is a shift worker and has some events
  if (s.profile?.workType === "shift worker" && events.length > 0) {
    const shifts = events.filter(e => e.type==="work" && e.title.toLowerCase().includes("shift")).length;
    if (shifts < 3) out.push({ id:"shifts", lvl:"warn", type:"work", msg:"Shift schedule looks sparse — add upcoming shifts to the Calendar for better planning" });
  }

  events.filter(e => e.date===TODAY).forEach(ev =>
    out.push({ id:`ev${ev.id}`, lvl:"info", type:"calendar", msg:`${ev.title}${ev.time ? " at "+ev.time : ""}` })
  );

  finance.filter(f => f.next && f.next !== "").forEach(f => {
    const d = df(f.next);
    if (d >= 0 && d <= 7) out.push({ id:`fp${f.id}`, lvl:d<=3?"warn":"info", type:"finance", msg:`${f.name} renews ${d===0?"today":d===1?"tomorrow":"in "+d+"d"} — $${f.amount}` });
  });

  tasks.filter(t => !t.done && t.created).forEach(t => {
    const age = -df(t.created);
    if (age > 7) out.push({ id:`st${t.id}`, lvl:"warn", type:"task", msg:`Stale task (${age}d): "${t.text.slice(0,40)}…"` });
  });

  // Only warn about workouts if user has logged at least one before
  if (workouts.length > 0) {
    const lastWorkout = -df(s.workouts[0].date);
    if (lastWorkout > 5) out.push({ id:"nowrk", lvl:"warn", type:"health", msg:`No workout logged in ${lastWorkout} days` });
  }

  const vinylInbound = vinyl.filter(v => !v.delivered && v.status==="shipped" && df(v.eta)<=7).length;
  if (vinylInbound >= 2) out.push({ id:"multi-vin", lvl:"info", type:"vinyl", msg:`${vinylInbound} vinyl deliveries arriving this week` });

  // Income/payday signals
  (s.income||[]).forEach(inc => {
    if (!inc.nextDate) return;
    const d = df(inc.nextDate);
    if (d >= 0 && d <= 3) out.push({ id:`pay${inc.id}`, lvl:"info", type:"finance", msg:`Payday${d===0?" today":d===1?" tomorrow":" in "+d+"d"} — ${inc.name} ${d<=1?"incoming":"coming up"}` });
  });

  // Net position warning — if expenses exceed income
  if ((s.income||[]).length > 0) {
    const monthlyInc = (s.income||[]).reduce((sum, i) => {
      const a = parseFloat(i.amount)||0;
      if (i.freq==="weekly") return sum + a*4.33;
      if (i.freq==="bi-weekly") return sum + a*2.167;
      if (i.freq==="annual") return sum + a/12;
      return sum + a;
    }, 0);
    const monthlyExp = finance.reduce((sum, f) => {
      const a = parseFloat(f.amount)||0;
      if (f.freq==="weekly") return sum + a*4.33;
      if (f.freq==="annual") return sum + a/12;
      return sum + a;
    }, 0);
    if (monthlyExp > monthlyInc * 0.9) {
      out.push({ id:"net-warn", lvl:"warn", type:"finance", msg:`Expenses ($${monthlyExp.toFixed(0)}/mo) approaching income ($${monthlyInc.toFixed(0)}/mo)` });
    }
  }

  // ── PROACTIVE HISTORICAL SIGNALS ──

  // Workout frequency baseline vs this week
  if (workouts.length >= 4) {
    const thisWeek4 = workouts.filter(w => {
      const age = -df(w.date); return age >= 0 && age <= 6;
    }).length;
    const last4Weeks = workouts.filter(w => {
      const age = -df(w.date); return age >= 7 && age <= 34;
    }).length;
    const avgPerWeek = last4Weeks / 4;
    if (avgPerWeek >= 2 && thisWeek4 === 0 && new Date().getDay() >= 3) {
      out.push({ id:"wrk-below-avg", lvl:"warn", type:"health",
        msg:`No workouts this week — your average is ${avgPerWeek.toFixed(1)}/week` });
    }
  }

  // Vinyl spend this month vs average
  if (vinyl.length >= 3) {
    const thisMonth = vinyl.filter(v => {
      const d = new Date(v.ordered || v.dateAdded || "");
      return !isNaN(d) && d.getMonth() === new Date().getMonth() && d.getFullYear() === new Date().getFullYear();
    }).length;
    const totalOrders = vinyl.filter(v => v.ordered || v.dateAdded).length;
    const monthsActive = Math.max(1, Math.ceil(-df(s.vinyl[vinyl.length-1]?.ordered || TODAY) / 30));
    const avgPerMonth = totalOrders / monthsActive;
    if (thisMonth > avgPerMonth * 1.5 && thisMonth >= 3) {
      out.push({ id:"vinyl-heavy", lvl:"warn", type:"vinyl",
        msg:`${thisMonth} vinyl orders this month — above your usual ${avgPerMonth.toFixed(1)}/month` });
    }
  }

  // Stale goals — no progress update in 2+ weeks
  goals.filter(g => computeGoalProgress(g) < 100).forEach(g => {
    if (g.updated) {
      const staleDays = -df(g.updated);
      if (staleDays >= 14) {
        out.push({ id:`stale-goal-${g.id}`, lvl:"warn", type:"goals",
          msg:`"${g.title.slice(0,35)}" — no progress update in ${staleDays} days` });
      }
    }
  });

  // Tasks piling up — more than 5 in today bucket
  const todayCount = tasks.filter(t => !t.done && t.bucket === "today").length;
  if (todayCount > 6) {
    out.push({ id:"task-overload", lvl:"warn", type:"task",
      msg:`${todayCount} tasks due today — consider deferring some to this week` });
  }

  // Upcoming race / key date from fitness goal
  const fitnessGoal = goals.find(g => g.cat === "fitness" && g.target);
  if (fitnessGoal && fitnessGoal.target) {
    const raceDate = new Date(fitnessGoal.target);
    if (!isNaN(raceDate)) {
      const daysToRace = df(raceDate.toISOString().slice(0,10));
      if (daysToRace >= 0 && daysToRace <= 21) {
        out.push({ id:"race-close", lvl:"warn", type:"health",
          msg:`${fitnessGoal.title} — ${daysToRace} day${daysToRace===1?"":"s"} away` });
      }
    }
  }

  return out;
}

function buildInsights(state, sigs) {
  const insights = [];
  if (!state.profile?.onboarded) return insights;

  const vinyl    = state.vinyl    || [];
  const tasks    = state.tasks    || [];
  const finance  = state.finance  || [];
  const events   = state.events   || [];
  const workouts = state.workouts || [];
  const goals    = state.goals    || [];
  const decisions = state.decisions || [];

  const todayEvts = events.filter(e => e.date===TODAY);
  if (todayEvts.filter(e=>e.type==="work").length >= 2 && todayEvts.filter(e=>e.type==="health").length === 0)
    insights.push({ type:"schedule", msg:"Heavy work day with no training scheduled. Consider blocking time for a workout." });

  const monthly = finance.filter(f=>f.freq==="monthly").reduce((s,f)=>s+f.amount,0);
  if (monthly > 140) insights.push({ type:"finance", msg:`Monthly recurring is $${monthly.toFixed(2)}. Worth reviewing for unused subscriptions.` });

  const inbound = vinyl.filter(v=>!v.delivered&&v.status==="shipped"&&df(v.eta)<=7);
  if (inbound.length >= 2) insights.push({ type:"vinyl", msg:`${inbound.length} records arriving this week. Confirm delivery addresses.` });

  const recentRuns = workouts.filter(w=>w.type.includes("Run")&&(-df(w.date))<=7).length;
  const fitnessGoal = goals.find(g=>g.cat==="fitness");
  if (fitnessGoal && recentRuns < 2) insights.push({ type:"health", msg:`${recentRuns} run${recentRuns===1?"":"s"} logged this week — your fitness goal may need attention.` });

  if (decisions.length > 0) insights.push({ type:"decision", msg:`${decisions.length} open decision${decisions.length===1?"":"s"} pending review.` });

  return insights;
}

function buildDailyBrief(state, sigs) {
  const tasks    = state.tasks    || [];
  const goals    = state.goals    || [];
  const finance  = state.finance  || [];
  const vinyl    = state.vinyl    || [];
  const events   = state.events   || [];
  const income   = state.income   || [];
  const sigsArr  = sigs           || [];

  const name     = state.profile?.name || "there";
  const workType = state.profile?.workType || "";

  const todayTasks    = tasks.filter(t => !t.done && t.bucket === "today");
  const doneToday     = tasks.filter(t => t.done && t.bucket === "today");
  const topPriorities = todayTasks.slice(0, 3).map(t => t.text);
  if (topPriorities.length === 0 && goals.length > 0) {
    goals.slice(0, 2).forEach(g => topPriorities.push(`${g.title} — ${Math.round(computeGoalProgress(g))}% complete`));
  }
  const nextStep = topPriorities.length > 0
    ? topPriorities[0]
    : doneToday.length > 0
      ? "All caught up today."
      : "Set your first task or goal to get started.";

  const monthlyInc = income.reduce((sum, i) => {
    const a = parseFloat(i.amount) || 0;
    if (i.freq === "weekly")     return sum + a * 4.33;
    if (i.freq === "bi-weekly")  return sum + a * 2.167;
    if (i.freq === "annual")     return sum + a / 12;
    return sum + a;
  }, 0);
  const monthlyExp = finance.reduce((sum, f) => {
    const a = parseFloat(f.amount) || 0;
    if (f.freq === "weekly")  return sum + a * 4.33;
    if (f.freq === "annual")  return sum + a / 12;
    return sum + a;
  }, 0);
  const nextPayday = income
    .filter(i => i.nextDate)
    .sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate))[0];

  const inTransit = vinyl.filter(v => !v.delivered && v.status === "shipped");
  return {
    date:      TODAY_DISP,
    name,
    shift:     workType ? `Work type: ${workType}` : "No schedule set",
    training:  goals.find(g => g.cat === "fitness")?.note || "",
    inTransit,
    priorities: topPriorities.length > 0
      ? topPriorities
      : ["Add your first task to get started", "Set a goal", "Add a recurring bill or subscription"],
    headsUp:   sigsArr.filter(s => s.lvl === "warn").slice(0, 3),
    todayEvts: events.filter(e => e.date === TODAY),
    payments:  finance.filter(f => { try { const d = df(f.next); return d >= 0 && d <= 10; } catch { return false; } }).slice(0, 4),
    nextStep,
    monthlyInc,
    monthlyExp,
    netMonthly: monthlyInc - monthlyExp,
    nextPayday,
  };
}

function buildSystemPrompt(state, sigs) {
  const profile = state.profile || {};
  const name = profile.name || "the user";
  const finance   = state.finance   || [];
  const tasks     = state.tasks     || [];
  const goals     = state.goals     || [];
  const vinyl     = state.vinyl     || [];
  const decisions = state.decisions || [];
  const memories  = state.memories  || [];
  const monthly = finance.filter(f => f.freq === "monthly").reduce((s, f) => s + f.amount, 0);
  const inTransit = vinyl.filter(v => !v.delivered && v.status === "shipped");
  const due = finance.filter(f => { const d = df(f.next); return d >= 0 && d <= 7; });
  const todayTasks = tasks.filter(t => !t.done && t.bucket === "today");
  return `You are Mylo, ${name}'s AI Chief of Staff. Brilliant, direct, warm. You think like a trusted friend who happens to be a world-class strategist, coach, and executive assistant — someone who knows this person's full context and genuinely wants them to win.

Core character: disciplined, stoic, growth-oriented. You believe discipline is freedom. You focus on what's controllable. You treat setbacks as data. You speak plainly and precisely — never corporate, never preachy, never vague.

Communication style: concise prose by default. Use point form only when listing 3+ distinct items. No markdown headers, no asterisks, no filler phrases. Get to the point, make it personal, move forward. Reference ${name}'s actual data — specific numbers, dates, goals — to make every response feel tailored. Use their name sparingly, like a real person would.

When relevant, bring in nutrition and health awareness naturally: protein targets, sleep, energy, recovery. Celebrate wins briefly and pivot to what's next.

TODAY: ${TODAY_DISP}
${profile.workType ? `WORK: ${profile.workType}` : ""}
${profile.payday ? `PAYDAY: ${profile.payday}` : ""}

PROFILE:
- Name: ${name}
${profile.workType ? `- Work type: ${profile.workType}` : "- Work schedule: not yet set"}
${profile.calProvider ? `- Calendar: ${profile.calProvider}` : ""}

TODAY'S TASKS: ${todayTasks.map(t => t.text).join(" | ") || "none"}
VINYL IN TRANSIT: ${inTransit.map(v => `${v.artist} — ${v.album} (ETA ${v.eta})`).join(" | ") || "none"}
MONTHLY INCOME: ${(state.income||[]).reduce((s,i)=>{const a=parseFloat(i.amount)||0;if(i.freq==="weekly")return s+a*4.33;if(i.freq==="bi-weekly")return s+a*2.167;if(i.freq==="annual")return s+a/12;return s+a;},0).toFixed(0)}
MONTHLY EXPENSES: $${monthly.toFixed(2)}
NET MONTHLY: $${((state.income||[]).reduce((s,i)=>{const a=parseFloat(i.amount)||0;if(i.freq==="weekly")return s+a*4.33;if(i.freq==="bi-weekly")return s+a*2.167;if(i.freq==="annual")return s+a/12;return s+a;},0)-monthly).toFixed(0)}
INCOME SOURCES: ${(state.income||[]).map(i=>`${i.name} ($${i.amount}/${i.freq}${i.nextDate?`, next ${i.nextDate}`:""})`).join(" | ") || "none set"}
DUE THIS WEEK: ${due.map(f => `${f.name} $${f.amount} on ${f.next}`).join(" | ") || "none"}
ACTIVE SIGNALS: ${sigs.map(s => s.msg).join(" | ") || "none"}
${(() => {
  const workouts = state.workouts || [];
  if (workouts.length === 0) return '';
  const stats = computeTrainStats(workouts, TODAY);
  const trainRoutines = state.trainRoutines || [];
  const raceGoal = (state.goals||[]).find(g => g.targetDate && (g.type==='race'||/race|marathon|half|5k|10k|triathlon/i.test(g.title||'')));
  const raceDate = raceGoal?.targetDate;
  const raceName = raceGoal?.title || 'race';
  const raceDays = raceDate ? Math.round((new Date(raceDate+'T12:00:00')-new Date(TODAY+'T12:00:00'))/86400000) : null;
  const recentW = workouts.slice(0,5).map(w=>`${w.date} ${w.type}${w.dist>0?` ${w.dist}mi`:''}`).join(', ');
  const routinesSummary = trainRoutines.map(r=>`${r.name} (${r.type}): ${r.exercises.map(e=>`${e.name} ${e.sets}x${e.reps}x${e.weight}lb`).join(', ')}`).join(' | ') || 'none';
  return `
TRAINING:
- Recent: ${recentW || 'none'}
- This week: ${stats.weeklyMiles} mi, ${stats.weeklyWorkoutCount} sessions
- Streak: ${stats.currentStreak} days | Lifetime: ${stats.lifetimeMiles} mi
- Race: ${raceDate ? `${raceName} on ${raceDate} (${raceDays} days away)` : 'none scheduled'}
- Saved routines: ${routinesSummary}`;
})()}
GOALS: ${goals.map(g => `${g.title} (${Math.round(computeGoalProgress(g))}%)`).join(" | ") || "none set"}
OPEN DECISIONS: ${decisions.filter(d=>d.status!=='resolved').map(d=>`[${d.domain||'general'}] ${d.title} — ${d.decision||'pending'}`).join(" | ") || "none"}

MEMORIES:
${memories.map(m => `[${m.cat}] ${m.text}`).join("\n") || "No memories stored yet."}


MEMORY RULE — Be proactive about storing facts. When the user shares ANY of these, silently add an add_memory action at the END of your response:
- Personal facts (race date, salary, relationship status, where they live, medical info)
- Professional context (job title, employer, colleagues, projects, immigration status)
- Long-term goals or targets (portfolio target, retirement age, race time)
- Preferences you'll need later (dietary restrictions, schedule patterns, vendors they like)
- Decisions made ("I decided to...", "I'm going with...", "I chose...")
- Named people and their context ("my department chair Dr Smith", "my attorney Maria")

For add_memory: key must be a short snake_case identifier like "marathon_date", "immigration_status", "retirement_target".
Don't store: transient status (what they're doing today), things you were just told to log, or information already in their state.`.trim();
}



// ─── PATTERN LEARNING ENGINE ──────────────────────────────────────────────────
// Detects behavioral patterns and surfaces proactive insights
function buildPatternInsights(state) {
  const insights = [];
  if (!state.profile?.onboarded) return insights;

  const today     = new Date();
  const todayStr  = TODAY;
  const workouts  = state.workouts || [];
  const tasks     = state.tasks    || [];
  const vinyl     = state.vinyl    || [];
  const finance   = state.finance  || [];
  const income    = state.income   || [];

  // ── Workout frequency pattern ───────────────────────────────────────────────
  if (workouts.length >= 6) {
    // Calculate rolling 4-week average
    const weeks = [0,1,2,3,4].map(w => {
      const start = new Date(today); start.setDate(today.getDate() - (w+1)*7);
      const end   = new Date(today); end.setDate(today.getDate() - w*7);
      const startStr = start.toISOString().slice(0,10);
      const endStr   = end.toISOString().slice(0,10);
      return workouts.filter(w => w.date >= startStr && w.date < endStr).length;
    });
    const avgPerWeek = weeks.slice(1).reduce((s,n)=>s+n,0) / 4;
    const thisWeek   = weeks[0];
    const dayOfWeek  = today.getDay(); // 0=Sun

    if (avgPerWeek >= 2 && thisWeek === 0 && dayOfWeek >= 3) {
      insights.push({
        id: 'pat-workout-gap',
        type: 'pattern',
        cat: 'health',
        lvl: 'warn',
        msg: `No workouts yet this week — your average is ${avgPerWeek.toFixed(1)}/week`,
        action: 'Log one now to stay on track',
      });
    } else if (thisWeek > avgPerWeek * 1.5 && thisWeek >= 4) {
      insights.push({
        id: 'pat-workout-high',
        type: 'pattern',
        cat: 'health',
        lvl: 'info',
        msg: `Strong week — ${thisWeek} workouts vs your usual ${avgPerWeek.toFixed(1)}/week`,
        action: null,
      });
    }

    // Streak detection
    let streak = 0;
    const sortedDates = [...new Set(workouts.map(w=>w.date))].sort().reverse();
    for (let i = 0; i < sortedDates.length; i++) {
      const expected = new Date(today);
      expected.setDate(today.getDate() - i);
      if (sortedDates[i] === expected.toISOString().slice(0,10)) streak++;
      else break;
    }
    if (streak >= 5) {
      insights.push({
        id: 'pat-streak',
        type: 'pattern',
        cat: 'health',
        lvl: 'info',
        msg: `${streak}-day workout streak — don't break the chain`,
        action: null,
      });
    }
  }

  // ── Task completion rate ────────────────────────────────────────────────────
  if (tasks.length >= 10) {
    const last30days = todayStr.slice(0,8) + String(parseInt(todayStr.slice(8))-30).padStart(2,'0');
    const recent = tasks.filter(t => t.created && t.created >= last30days);
    const done   = recent.filter(t => t.done).length;
    const rate   = recent.length > 0 ? Math.round((done / recent.length) * 100) : 0;
    const overdue = tasks.filter(t => !t.done && t.dueDate && t.dueDate < todayStr).length;

    if (overdue >= 3) {
      insights.push({
        id: 'pat-overdue',
        type: 'pattern',
        cat: 'tasks',
        lvl: 'warn',
        msg: `${overdue} overdue tasks — your completion rate is ${rate}% this month`,
        action: 'Review and reschedule',
      });
    } else if (rate >= 80 && recent.length >= 8) {
      insights.push({
        id: 'pat-completion',
        type: 'pattern',
        cat: 'tasks',
        lvl: 'info',
        msg: `${rate}% task completion this month — strong consistency`,
        action: null,
      });
    }
  }

  // ── Vinyl spend pattern ─────────────────────────────────────────────────────
  if (vinyl.length >= 4) {
    const months = [0,1,2].map(m => {
      const d = new Date(today); d.setMonth(today.getMonth() - m);
      const prefix = d.toISOString().slice(0,7);
      return vinyl.filter(v => (v.ordered||v.dateAdded||'').startsWith(prefix)).length;
    });
    const avgPerMonth = (months[1] + months[2]) / 2;
    const thisMonth   = months[0];
    if (avgPerMonth >= 1 && thisMonth > avgPerMonth * 2 && thisMonth >= 3) {
      const spend = vinyl
        .filter(v => (v.ordered||v.dateAdded||'').startsWith(today.toISOString().slice(0,7)))
        .reduce((s,v) => s + (parseFloat(v.price)||0), 0);
      insights.push({
        id: 'pat-vinyl-spend',
        type: 'pattern',
        cat: 'vinyl',
        lvl: 'warn',
        msg: `${thisMonth} records this month vs your usual ${avgPerMonth.toFixed(1)} — heavy month${spend>0?' ($'+spend.toFixed(0)+')':''}`,
        action: null,
      });
    }
  }

  // ── Income vs expense drift ─────────────────────────────────────────────────
  if (income.length > 0 && finance.length > 0) {
    const monthlyInc = income.reduce((s,i) => {
      const a = parseFloat(i.amount)||0;
      if (i.freq==='bi-weekly') return s+a*2.167;
      if (i.freq==='weekly')    return s+a*4.33;
      if (i.freq==='annual')    return s+a/12;
      return s+a;
    }, 0);
    const monthlyExp = finance.filter(f=>f.freq==='monthly').reduce((s,f)=>s+(parseFloat(f.amount)||0), 0);
    const ratio = monthlyExp / monthlyInc;
    if (ratio > 0.85 && ratio < 2) {
      insights.push({
        id: 'pat-expense-ratio',
        type: 'pattern',
        cat: 'finance',
        lvl: 'warn',
        msg: `Expenses are ${Math.round(ratio*100)}% of income — tight margin`,
        action: 'Review subscriptions',
      });
    }
  }

  return insights;
}


function computeMomentumStreaks(state) {
  const workouts = state.workouts || [];
  const tasks    = state.tasks    || [];
  const habits   = state.habits   || [];
  function calcStreak(dates) {
    if (!dates.length) return 0;
    const unique = [...new Set(dates)].sort().reverse();
    const today = new Date(); let streak = 0;
    for (let i = 0; i < 90; i++) {
      const d = new Date(today); d.setDate(today.getDate() - i);
      const ds = d.toISOString().slice(0,10);
      if (unique.includes(ds)) streak++; else if (i > 0) break;
    }
    return streak;
  }
  const wD = workouts.map(w=>w.date).filter(Boolean);
  const tD = tasks.filter(t=>t.done&&t.completedAt).map(t=>t.completedAt);
  const hD = habits.flatMap(h=>(h.log||[]).filter(l=>l.done).map(l=>l.date)).filter(Boolean);
  const last7 = Array.from({length:7},(_,i)=>{
    const d=new Date(); d.setDate(d.getDate()-(6-i));
    const ds=d.toISOString().slice(0,10);
    return {date:ds,workout:wD.includes(ds),task:tD.includes(ds),habit:hD.includes(ds),
      active:wD.includes(ds)||tD.includes(ds)||hD.includes(ds)};
  });
  return {workoutStreak:calcStreak(wD),taskStreak:calcStreak(tD),habitStreak:calcStreak(hD),
    overallStreak:Math.max(calcStreak(wD),calcStreak(tD),calcStreak(hD)),last7};
}

// ─── PROACTIVE ALERTS ENGINE ──────────────────────────────────────────────────
function buildProactiveAlerts(state) {
  const alerts  = [];
  const tasks   = state.tasks    || [];
  const finance = state.finance  || [];
  const income  = state.income   || [];
  const goals   = state.goals    || [];
  const workouts= state.workouts || [];
  const now     = new Date();
  const hour    = now.getHours();

  const upcomingBills = finance.filter(f => f.next).filter(f => {
    const d = df(f.next); return d >= 0 && d <= 4;
  }).sort((a, b) => df(a.next) - df(b.next));

  if (upcomingBills.length >= 2) {
    const total = upcomingBills.reduce((s, f) => s + parseFloat(f.amount||0), 0);
    alerts.push({ domain: 'finance', urgency: 'warn', title: 'bills coming up',
      body: `${upcomingBills.length} bills totaling ${total.toFixed(0)} due in the next 4 days` });
  } else if (upcomingBills.length === 1) {
    const b = upcomingBills[0]; const d = df(b.next);
    alerts.push({ domain: 'finance', urgency: d <= 1 ? 'warn' : 'info', title: b.name,
      body: `${b.amount} due ${d===0?'today':d===1?'tomorrow':'in '+d+'d'}` });
  }

  const nextPay = income.filter(i => i.nextDate)
    .sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate))[0];
  if (nextPay?.nextDate) {
    const pd = df(nextPay.nextDate);
    if (pd === 0) alerts.push({ domain: 'finance', urgency: 'info', title: 'payday', body: 'payday today' });
    if (pd === 1) alerts.push({ domain: 'finance', urgency: 'info', title: 'payday tomorrow', body: nextPay.name });
  }

  const raceDate = state.raceDate || (goals.find(g => g.targetDate && /marathon|race|5k|10k|half|triathlon/i.test(g.title||''))?.targetDate);
  if (raceDate) {
    const raceDays = Math.round((new Date(raceDate + 'T12:00:00') - now) / 86400000);
    if (raceDays >= 0 && raceDays <= 14) {
      const lastWkt = workouts.length > 0 ? Math.abs(df(workouts[0].date)) : 99;
      if (lastWkt >= 3)
        alerts.push({ domain: 'health', urgency: 'warn', title: 'training gap',
          body: `race in ${raceDays}d - last workout was ${lastWkt} days ago` });
      else
        alerts.push({ domain: 'health', urgency: 'info', title: 'race week',
          body: `${raceDays} day${raceDays!==1?'s':''} to race` });
    }
  }

  if (workouts.length >= 6 && hour >= 14) {
    const thisWeek = workouts.filter(w => df(w.date) <= 0 && df(w.date) >= -6).length;
    const last4Avg = workouts.filter(w => df(w.date) >= -34 && df(w.date) < -6).length / 4;
    if (last4Avg >= 2 && thisWeek === 0 && now.getDay() >= 3)
      alerts.push({ domain: 'health', urgency: 'warn', title: 'workout gap',
        body: `no workouts this week - your average is ${last4Avg.toFixed(1)}/week` });
  }

  const overdue = tasks.filter(t => !t.done && t.dueDate && df(t.dueDate) < 0).length;
  if (overdue >= 3)
    alerts.push({ domain: 'tasks', urgency: 'warn', title: 'overdue tasks',
      body: `${overdue} tasks past their due date` });

  return alerts.sort((a, b) => (a.urgency==='warn'?0:1) - (b.urgency==='warn'?0:1));
}

// ─── PATTERN MEMORY ENGINE (Phase 3A) ─────────────────────────────────────────
// Detects cross-domain behavioral correlations over 8+ weeks.
// Returns array of memory objects to store in st.memories[].
// Safe to call weekly — deduplicates by key.
function buildPatternMemories(state) {
  const memories = [];
  if (!state.profile?.onboarded) return memories;

  const workouts = state.workouts || [];
  const events   = state.events   || [];
  const tasks    = state.tasks    || [];
  const income   = state.income   || [];
  const vinyl    = state.vinyl    || [];
  const now      = new Date();

  // Need at least 8 weeks of data for meaningful patterns
  if (workouts.length < 8 && tasks.length < 20) return memories;

  // Helper: get week string for a date (YYYY-WW)
  function weekOf(dateStr) {
    const d = new Date(dateStr + 'T12:00:00');
    const jan1 = new Date(d.getFullYear(), 0, 1);
    const wk = Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
    return d.getFullYear() + '-W' + String(wk).padStart(2, '0');
  }

  // Build per-week buckets for last 12 weeks
  const weeks = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now); d.setDate(d.getDate() - i * 7);
    const start = new Date(d); start.setDate(d.getDate() - d.getDay());
    const end   = new Date(start); end.setDate(start.getDate() + 6);
    const s = start.toISOString().slice(0, 10);
    const e = end.toISOString().slice(0, 10);
    return {
      start: s, end: e,
      workouts:  workouts.filter(w => w.date >= s && w.date <= e).length,
      shifts:    events.filter(ev => ev.date >= s && ev.date <= e && /shift|work|er |nocturnist|overnight/i.test((ev.title || '') + (ev.type || ''))).length,
      tasksDone: tasks.filter(t => t.done && t.completedAt && t.completedAt >= s && t.completedAt <= e).length,
      spend:     (state.transactions || []).filter(tx => tx.date >= s && tx.date <= e).reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0),
      payday:    income.some(inc => inc.nextDate && inc.nextDate >= s && inc.nextDate <= e),
    };
  }).reverse(); // oldest first

  const validWeeks = weeks.filter(w => w.workouts > 0 || w.tasksDone > 0 || w.shifts > 0);
  if (validWeeks.length < 6) return memories;

  // ── Correlation 1: Shifts → Workout suppression ──────────────────────────
  const shiftWeeks   = validWeeks.filter(w => w.shifts >= 3);
  const noShiftWeeks = validWeeks.filter(w => w.shifts < 2);
  if (shiftWeeks.length >= 3 && noShiftWeeks.length >= 3) {
    const shiftAvg   = shiftWeeks.reduce((s, w) => s + w.workouts, 0) / shiftWeeks.length;
    const noShiftAvg = noShiftWeeks.reduce((s, w) => s + w.workouts, 0) / noShiftWeeks.length;
    if (noShiftAvg > 0 && shiftAvg < noShiftAvg * 0.6) {
      memories.push({
        key: 'pattern_shift_workout',
        cat: 'pattern',
        text: `Workout frequency drops on weeks with 3+ shifts (avg ${shiftAvg.toFixed(1)} vs ${noShiftAvg.toFixed(1)} on lighter weeks)`,
        detectedAt: TODAY,
        domain: 'health',
      });
    }
  }

  // ── Correlation 2: Workouts → Task completion ────────────────────────────
  const highWorkoutWeeks = validWeeks.filter(w => w.workouts >= 3);
  const lowWorkoutWeeks  = validWeeks.filter(w => w.workouts <= 1);
  if (highWorkoutWeeks.length >= 3 && lowWorkoutWeeks.length >= 3) {
    const highTaskAvg = highWorkoutWeeks.reduce((s, w) => s + w.tasksDone, 0) / highWorkoutWeeks.length;
    const lowTaskAvg  = lowWorkoutWeeks.reduce((s, w) => s + w.tasksDone, 0) / lowWorkoutWeeks.length;
    if (lowTaskAvg > 0 && highTaskAvg > lowTaskAvg * 1.3) {
      memories.push({
        key: 'pattern_workout_tasks',
        cat: 'pattern',
        text: `Task completion is ${Math.round((highTaskAvg / lowTaskAvg - 1) * 100)}% higher on weeks with 3+ workouts (${highTaskAvg.toFixed(1)} vs ${lowTaskAvg.toFixed(1)} tasks done)`,
        detectedAt: TODAY,
        domain: 'tasks',
      });
    }
  }

  // ── Correlation 3: Payday → Spend spike ─────────────────────────────────
  const paydayWeeks   = validWeeks.filter(w => w.payday && w.spend > 0);
  const noPaydayWeeks = validWeeks.filter(w => !w.payday && w.spend > 0);
  if (paydayWeeks.length >= 2 && noPaydayWeeks.length >= 3) {
    const paydaySpend   = paydayWeeks.reduce((s, w) => s + w.spend, 0) / paydayWeeks.length;
    const noPaydaySpend = noPaydayWeeks.reduce((s, w) => s + w.spend, 0) / noPaydayWeeks.length;
    if (noPaydaySpend > 0 && paydaySpend > noPaydaySpend * 1.4) {
      memories.push({
        key: 'pattern_payday_spend',
        cat: 'pattern',
        text: `Spending spikes on payday weeks (avg ${Math.round(paydaySpend)} vs ${Math.round(noPaydaySpend)} on other weeks)`,
        detectedAt: TODAY,
        domain: 'finance',
      });
    }
  }

  // ── Trend 1: Workout volume trending ────────────────────────────────────
  if (validWeeks.length >= 8) {
    const first4 = validWeeks.slice(0, 4).reduce((s, w) => s + w.workouts, 0) / 4;
    const last4  = validWeeks.slice(-4).reduce((s, w) => s + w.workouts, 0) / 4;
    if (first4 > 0 && last4 < first4 * 0.5 && first4 >= 2) {
      memories.push({
        key: 'pattern_workout_trend_down',
        cat: 'pattern',
        text: `Workout frequency has trended down over the past 8 weeks (${first4.toFixed(1)}/wk to ${last4.toFixed(1)}/wk)`,
        detectedAt: TODAY,
        domain: 'health',
      });
    } else if (first4 > 0 && last4 > first4 * 1.5 && last4 >= 2) {
      memories.push({
        key: 'pattern_workout_trend_up',
        cat: 'pattern',
        text: `Workout frequency has trended up over the past 8 weeks (${first4.toFixed(1)}/wk to ${last4.toFixed(1)}/wk) — momentum building`,
        detectedAt: TODAY,
        domain: 'health',
      });
    }
  }

  return memories;
}

export { buildSignals, buildInsights, buildDailyBrief, buildSystemPrompt, buildPatternInsights, computeMomentumStreaks, buildProactiveAlerts, buildPatternMemories };
